import type { ReactNode, Ref } from 'react';
import {
    type FieldPath,
    type FieldValues,
    type UseFormReturn,
    useController,
} from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import { createPalette, PaletteButton, PalettePositionWidth } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ButtonGroup,
    type ButtonProps,
    Description,
    DialogTrigger,
    FieldControllerProvider,
    FieldError,
    FieldGroup,
    Group,
    Label,
    type OverlayTriggerState,
} from '@gitbook/spine-react';

import { getSpacePaletteItem, SpaceIcon } from '../../Space';

export interface SpacePickerFieldProps<TFieldValues extends FieldValues>
    extends Omit<SpacePickerProps, 'onChange' | 'value' | 'onBlur'> {
    form: UseFormReturn<TFieldValues>;
    name: FieldPath<TFieldValues>;
}

export function SpacePickerField<TFieldValues extends FieldValues>(
    props: SpacePickerFieldProps<TFieldValues>
) {
    const { form, name, spaces, label = 'Content', disabled, onlyCreateIfEmpty } = props;
    const controller = useController({
        control: form.control,
        name,
        rules: { required: 'Required' },
    });
    const { field } = controller;
    return (
        <FieldControllerProvider controller={controller}>
            <SpacePicker
                spaces={spaces}
                label={label}
                description={props.description}
                createSpaceDialogTrigger={props.createSpaceDialogTrigger}
                fieldRef={field.ref}
                onBlur={field.onBlur}
                onChange={(value) => {
                    field.onChange(value);
                }}
                value={field.value}
                disabled={disabled || field.disabled}
                onlyCreateIfEmpty={onlyCreateIfEmpty}
            />
        </FieldControllerProvider>
    );
}

export interface SpacePickerProps extends Pick<ButtonProps, 'onBlur' | 'disabled'> {
    orientation?: 'horizontal' | 'vertical';
    spaces: api.Space[];
    label?: string;
    description: ReactNode;
    createSpaceDialogTrigger: OverlayTriggerState;
    fieldRef?: Ref<HTMLButtonElement>;
    onChange: (value: string) => void;
    value: string;
    /**
     * Do not show a palette is the space is not set.
     */
    onlyCreateIfEmpty?: boolean;
}

export function SpacePicker(props: SpacePickerProps) {
    const {
        spaces,
        label = 'Content',
        disabled,
        createSpaceDialogTrigger,
        fieldRef,
        onBlur,
        value,
        onChange,
        onlyCreateIfEmpty,
    } = props;
    const space = value ? spaces.find((space) => space.id === value) : null;
    const buttonProps = {
        ref: fieldRef,
        onBlur,
        label: space ? space.title : 'Select space',
        kind: 'secondary' as const,
        className: 'self-start',
        disabled,
        leadingIcon: space ? <SpaceIcon space={space} /> : undefined,
        trailingIcon: Icon.ChevronDown,
    };
    return (
        <FieldGroup orientation={props.orientation}>
            <Group>
                <Label>{label}</Label>
                <Description>{props.description}</Description>
            </Group>
            <Group>
                <div className="flex items-center gap-2">
                    <ButtonGroup kind="segmented">
                        {onlyCreateIfEmpty && !space ? (
                            <DialogTrigger trigger={createSpaceDialogTrigger}>
                                <Button {...buttonProps} trailingIcon={undefined} />
                            </DialogTrigger>
                        ) : (
                            <PaletteButton
                                {...buttonProps}
                                palette={() => {
                                    return createPalette({
                                        position: { width: PalettePositionWidth.LINKS },
                                        search: { placeholder: 'Find space…' },
                                        groups: [
                                            {
                                                key: 'create-space',
                                                commands: () => {
                                                    return [
                                                        {
                                                            key: 'create-space',
                                                            icon: Icon.Plus,
                                                            title: 'Create new space',
                                                            trigger: () =>
                                                                createSpaceDialogTrigger.open(),
                                                        },
                                                    ];
                                                },
                                            },
                                            {
                                                key: 'spaces',
                                                commands: () => {
                                                    return spaces.map((space) => ({
                                                        ...getSpacePaletteItem(space),
                                                        trigger: () => onChange(space.id),
                                                    }));
                                                },
                                            },
                                        ],
                                    });
                                }}
                            />
                        )}

                        {space ? (
                            <Button
                                iconOnly
                                href={{ screen: { type: 'space', space: space.id } }}
                                label="Go to space"
                                kind="secondary"
                                leadingIcon={Icon.ArrowUpRight}
                            />
                        ) : null}
                    </ButtonGroup>
                </div>
                <FieldError />
            </Group>
        </FieldGroup>
    );
}
